/**
 * API Keys tab for the Settings page.
 *
 * Displays an explanatory introduction, a "Create" button, a refresh
 * button, and a table of the user's API keys with Revoke and Delete
 * actions.
 *
 * @module components/ApiKeysTab
 */

import React, { useState,
                useEffect,
                useCallback,
                useRef }        from 'react';
import { Trash2,
         ShieldOff,
         RefreshCw,
         Copy }                 from 'lucide-react';
import Modal                    from './Modal';
import { useI18n }              from '../dataProviders/I18nProvider';
import apiClient                from '../procedural/apiClient';
import * as constants           from '../constants';

/**
 * Format a date string or Date object for display.
 *
 * @param {string|Date} value - ISO date string or Date.
 * @returns {string} Formatted date string.
 */
function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleDateString('en-US', {
    year:  'numeric',
    month: 'long',
    day:   'numeric',
  });
}

/**
 * Truncate a UUID to show only the last 4 hex characters, prefixed
 * with an ellipsis.
 *
 * @param {string} id - Full UUID.
 * @returns {string} Truncated display string.
 */
function truncateId(id) {
  if (!id || id.length <= 4) return id || '';
  return '\u2026' + id.slice(-4);
}

/**
 * Renders the API Keys settings tab.
 *
 * @returns {React.ReactElement}
 */
export default function ApiKeysTab() {
  const { t } = useI18n();

  const [apiKeys, setApiKeys]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [copiedKeyId, setCopiedKeyId]         = useState(null);
  const copiedTimerRef                        = useRef(null);

  /**
   * Copy an API key to the clipboard and show a short-lived tooltip.
   *
   * @param {string} id - API key UUID.
   */
  function handleCopy(id) {
    navigator.clipboard.writeText(id);
    setCopiedKeyId(id);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopiedKeyId(null), 1500);
  }

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  /* ---- Load API keys ---------------------------------------------------- */

  const loadApiKeys = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/api-keys');
      setApiKeys(res.data.apiKeys);
    } catch {
      setError(t('settings.apiKeys.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadApiKeys(); }, [loadApiKeys]);

  /* ---- Revoke ----------------------------------------------------------- */

  /**
   * @param {object} key - API key record.
   */
  async function handleRevoke(key) {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.put(`/api/api-keys/${key.id}/revoke`);
      setApiKeys((prev) => prev.map((k) =>
        k.id === key.id ? { ...k, revoked: true } : k,
      ));
      setSuccessMsg(t('settings.apiKeys.revoked'));
    } catch {
      setError(t('settings.apiKeys.error.revoke'));
    }
  }

  /* ---- Create callback -------------------------------------------------- */

  /**
   * @param {object} newKey - Newly created API key record from server.
   */
  function handleCreated(newKey) {
    setShowCreateModal(false);
    setApiKeys((prev) => [...prev, newKey]);
  }

  /* ---- Delete callback -------------------------------------------------- */

  function handleDeleted(keyId) {
    setDeleteTarget(null);
    setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
  }

  /* ---- Render ----------------------------------------------------------- */

  return (
    <div className="settings-emails">
      <div className="settings-emails-intro">
        <p>{t('settings.apiKeys.intro')}</p>
      </div>

      <div className="settings-emails-button-row">
        <button
          type="button"
          className="settings-emails-create-btn"
          onClick={() => { setShowCreateModal(true); setError(null); setSuccessMsg(null); }}
        >
          {t('settings.apiKeys.createButton')}
        </button>
        <button
          type="button"
          className="settings-emails-refresh-btn"
          title={t('settings.apiKeys.refresh')}
          onClick={() => { setError(null); setSuccessMsg(null); loadApiKeys(); }}
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {(error || successMsg) && (
        <div className={successMsg ? 'auth-success' : 'auth-error'} style={{ marginTop: '12px' }}>
          {successMsg || error}
        </div>
      )}

      {loading && <p style={{ padding: '16px 0' }}>Loading…</p>}

      {!loading && apiKeys.length === 0 && (
        <p className="settings-emails-empty">{t('settings.apiKeys.noKeys')}</p>
      )}

      {!loading && apiKeys.length > 0 && (
        <table className="settings-emails-table">
          <thead>
            <tr>
              <th>{t('settings.apiKeys.col.key')}</th>
              <th>{t('settings.apiKeys.col.description')}</th>
              <th>{t('settings.apiKeys.col.createdAt')}</th>
              <th>{t('settings.apiKeys.col.expiresAt')}</th>
              <th>{t('settings.apiKeys.col.lastUsedAt')}</th>
              <th>{t('settings.apiKeys.col.revoked')}</th>
              <th>{t('settings.apiKeys.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key) => (
              <tr key={key.id}>
                <td className="settings-apikeys-key-cell" style={{ textAlign: 'center' }}>
                  <code className="settings-apikeys-key-value">{key.id}</code>
                  <span className="settings-apikeys-copy-wrapper">
                    <button
                      type="button"
                      className="settings-emails-action-btn"
                      title={t('settings.apiKeys.action.copy')}
                      onClick={() => handleCopy(key.id)}
                    >
                      <Copy size={14} />
                    </button>
                    {copiedKeyId === key.id && (
                      <span className="settings-apikeys-copied-tooltip">
                        {t('settings.apiKeys.copied')}
                      </span>
                    )}
                  </span>
                </td>
                <td>{key.description}</td>
                <td>{formatDate(key.createdAt)}</td>
                <td>{formatDate(key.expiresAt)}</td>
                <td>{key.lastUsedAt ? formatDate(key.lastUsedAt) : t('settings.apiKeys.never')}</td>
                <td>{key.revoked ? t('settings.apiKeys.yes') : t('settings.apiKeys.no')}</td>
                <td className="settings-emails-actions">
                  {!key.revoked && (
                    <button
                      type="button"
                      className="settings-emails-action-btn"
                      title={t('settings.apiKeys.action.revoke')}
                      onClick={() => { handleRevoke(key); }}
                    >
                      <ShieldOff size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="settings-emails-action-btn settings-emails-action-btn--danger"
                    title={t('settings.apiKeys.action.delete')}
                    onClick={() => { setDeleteTarget(key); setError(null); setSuccessMsg(null); }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
      {deleteTarget && (
        <DeleteApiKeyModal
          apiKey={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  Create API Key Modal                                                   */
/* ----------------------------------------------------------------------- */

/**
 * Modal for creating a new API key.
 *
 * @param {object}   props
 * @param {Function} props.onClose   - Dismiss callback.
 * @param {Function} props.onCreated - Called with the new API key record on success.
 * @returns {React.ReactElement}
 */
function CreateApiKeyModal({ onClose, onCreated }) {
  const { t } = useI18n();

  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt]     = useState('');
  const [error, setError]             = useState(null);
  const [submitting, setSubmitting]   = useState(false);

  /**
   * @param {React.FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!description || !expiresAt) return;

    setSubmitting(true);
    try {
      const res = await apiClient.post('/api/api-keys', {
        description,
        expiresAt: new Date(expiresAt).toISOString(),
      });
      onCreated(res.data.apiKey);
    } catch {
      setError(t('settings.apiKeys.error.create'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.apiKeys.createModal.title')} onClose={onClose}>
      <form className="auth-form" onSubmit={handleSubmit}>
        {error && <div className="auth-error">{error}</div>}

        <div className="auth-field">
          <label className="auth-label" htmlFor="create-apikey-desc">
            {t('settings.apiKeys.createModal.description.label')}
          </label>
          <input
            id="create-apikey-desc"
            className="auth-input"
            type="text"
            maxLength={constants.MAX_API_KEY_DESC_LENGTH}
            placeholder={t('settings.apiKeys.createModal.description.placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="create-apikey-expires">
            {t('settings.apiKeys.createModal.expiresAt.label')}
          </label>
          <input
            id="create-apikey-expires"
            className="auth-input"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
            {t('settings.apiKeys.createModal.cancel')}
          </button>
          <button type="submit" className="modal-btn modal-btn--primary" disabled={submitting || !description || !expiresAt}>
            {t('settings.apiKeys.createModal.ok')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------------------------- */
/*  Delete API Key Modal                                                   */
/* ----------------------------------------------------------------------- */

/**
 * Confirmation modal for deleting an API key.
 *
 * @param {object}   props
 * @param {object}   props.apiKey   - The API key record to delete.
 * @param {Function} props.onClose  - Dismiss callback.
 * @param {Function} props.onDeleted - Called with the key ID on success.
 * @returns {React.ReactElement}
 */
function DeleteApiKeyModal({ apiKey, onClose, onDeleted }) {
  const { t } = useI18n();

  const [error, setError]           = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete() {
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.delete(`/api/api-keys/${apiKey.id}`);
      onDeleted(apiKey.id);
    } catch {
      setError(t('settings.apiKeys.error.delete'));
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.apiKeys.deleteModal.title')} onClose={onClose}>
      <p style={{ color: 'var(--color-danger)', marginBottom: '12px', lineHeight: 1.6 }}>
        {t('settings.apiKeys.deleteModal.warning')}
      </p>
      <p style={{ fontWeight: 600, marginBottom: '4px' }}>
        {apiKey.description} ({truncateId(apiKey.id)})
      </p>

      {error && <div className="auth-error" style={{ marginTop: '12px' }}>{error}</div>}

      <div className="modal-actions">
        <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
          {t('settings.apiKeys.deleteModal.cancel')}
        </button>
        <button type="button" className="modal-btn modal-btn--danger" onClick={handleDelete} disabled={submitting}>
          {t('settings.apiKeys.deleteModal.delete')}
        </button>
      </div>
    </Modal>
  );
}
